import React, { useState, useCallback, useRef } from 'react';
import {
  Upload, Copy, Check, Loader2, AlertCircle, ScanText, RotateCcw,
  Download, RefreshCw, Lightbulb, Languages, FileText, Hash, Type
} from 'lucide-react';
import { API_CONFIG, formatFileSize } from '../config/api';

interface OcrResult {
  text: string;
  confidence?: number;
}

const LANGUAGES = [
  { code: 'eng', label: 'English' },
  { code: 'spa', label: 'Spanish' },
  { code: 'fra', label: 'French' },
  { code: 'deu', label: 'German' },
  { code: 'ita', label: 'Italian' },
  { code: 'por', label: 'Portuguese' },
  { code: 'chi_sim', label: 'Chinese (Simplified)' },
  { code: 'jpn', label: 'Japanese' },
  { code: 'kor', label: 'Korean' },
  { code: 'ara', label: 'Arabic' },
  { code: 'hin', label: 'Hindi' },
  { code: 'rus', label: 'Russian' },
];

const ImageOcr: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [extractedText, setExtractedText] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [language, setLanguage] = useState('eng');
  const [confidence, setConfidence] = useState<number | null>(null);
  const [showTips, setShowTips] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleFileSelect = useCallback((selectedFiles: FileList | null) => {
    if (!selectedFiles || selectedFiles.length === 0) return;
    const f = selectedFiles[0];
    if (!f.type.startsWith('image/')) {
      setError('Please select an image file.');
      return;
    }
    setFile(f);
    setError(null);
    setExtractedText('');
    setConfidence(null);
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target?.result as string);
    reader.readAsDataURL(f);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    handleFileSelect(e.dataTransfer.files);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const processOcr = async () => {
    if (!file) return;
    setIsProcessing(true);
    setError(null);
    setExtractedText('');
    setConfidence(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('language', language);

      const response = await fetch(`${API_CONFIG.BASE_URL}/imagetool/ocr`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'OCR processing failed');
      }

      const data: OcrResult = await response.json();
      setExtractedText(data.text || '');
      if (data.confidence !== undefined) {
        setConfidence(data.confidence);
      }
    } catch (err) {
      if (err instanceof TypeError && err.message.includes('fetch')) {
        setError('OCR service is temporarily unavailable. Please try again later.');
      } else {
        setError(
          err instanceof Error
            ? err.message
            : 'OCR processing failed. Please try again.'
        );
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const copyToClipboard = async () => {
    if (!extractedText) return;
    try {
      await navigator.clipboard.writeText(extractedText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
      if (textareaRef.current) {
        textareaRef.current.select();
        document.execCommand('copy');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    }
  };

  const downloadAsTxt = () => {
    if (!extractedText) return;
    const blob = new Blob([extractedText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const name = file?.name.replace(/\.[^.]+$/, '') || 'extracted';
    link.download = `${name}_text.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const reset = () => {
    setFile(null);
    setPreview(null);
    setExtractedText('');
    setError(null);
    setConfidence(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const wordCount = extractedText.trim() ? extractedText.trim().split(/\s+/).length : 0;
  const charCount = extractedText.length;

  const getConfidenceColor = (conf: number) => {
    if (conf >= 80) return 'text-green-600';
    if (conf >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getConfidenceLabel = (conf: number) => {
    if (conf >= 80) return 'High';
    if (conf >= 60) return 'Medium';
    return 'Low';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="flex justify-center mb-4">
          <div className="p-3 bg-blue-100 rounded-full">
            <ScanText className="w-8 h-8 text-blue-600" />
          </div>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Image OCR</h2>
        <p className="text-gray-600 max-w-lg mx-auto">
          Extract text from images using optical character recognition. Supports multiple languages.
        </p>
      </div>

      {/* Upload Area */}
      {!file && (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-blue-400 transition-colors cursor-pointer"
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Drop an image here or click to select
          </h3>
          <p className="text-gray-500 text-sm">
            Supports PNG, JPG, WebP up to {formatFileSize(API_CONFIG.MAX_FILE_SIZE)}
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={(e) => handleFileSelect(e.target.files)}
            className="hidden"
          />
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-red-700">{error}</p>
            <button
              onClick={processOcr}
              className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-red-600 hover:text-red-800 transition-colors"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Content */}
      {file && (
        <div className="space-y-4">
          {/* Language selector and file info */}
          <div className="bg-gray-50 rounded-xl p-4 flex flex-col sm:flex-row gap-4">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="w-10 h-10 rounded-lg overflow-hidden bg-gray-200 flex-shrink-0">
                {preview && <img src={preview} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
              </div>
            </div>
            <div className="flex-shrink-0">
              <label className="block text-xs font-medium text-gray-600 mb-1 flex items-center gap-1">
                <Languages className="w-3.5 h-3.5" /> Language
              </label>
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="block w-full sm:w-48 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {LANGUAGES.map(lang => (
                  <option key={lang.code} value={lang.code}>{lang.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Main content: Image + Text */}
          <div className="grid md:grid-cols-2 gap-4">
            {/* Image Preview */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <FileText className="w-4 h-4" /> Source Image
              </h3>
              <div className="bg-gray-100 rounded-xl overflow-hidden flex items-center justify-center min-h-[300px] max-h-[500px]">
                {preview && (
                  <img src={preview} alt="Uploaded" className="max-w-full max-h-[500px] object-contain" />
                )}
              </div>
            </div>

            {/* Extracted Text */}
            <div className="flex flex-col">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Type className="w-4 h-4" /> Extracted Text
                </h3>
                <div className="flex items-center gap-2">
                  {confidence !== null && (
                    <span className={`text-xs font-medium ${getConfidenceColor(confidence)}`}>
                      Confidence: {confidence.toFixed(0)}% ({getConfidenceLabel(confidence)})
                    </span>
                  )}
                </div>
              </div>

              <div className="flex-1 relative">
                {isProcessing ? (
                  <div className="flex flex-col items-center justify-center h-full min-h-[300px] bg-gray-50 rounded-xl border border-gray-200">
                    <Loader2 className="w-8 h-8 text-blue-600 animate-spin mb-3" />
                    <span className="text-gray-600 font-medium">Extracting text...</span>
                    <span className="text-gray-400 text-sm mt-1">This may take a moment</span>
                  </div>
                ) : (
                  <textarea
                    ref={textareaRef}
                    value={extractedText || (file && !isProcessing && !error ? 'Click "Extract Text" to begin' : '')}
                    onChange={(e) => setExtractedText(e.target.value)}
                    placeholder="Extracted text will appear here..."
                    className={`w-full h-full min-h-[300px] p-4 bg-white border border-gray-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm font-mono ${
                      !extractedText ? 'text-gray-400' : 'text-gray-900'
                    }`}
                  />
                )}
              </div>

              {/* Stats bar */}
              {extractedText && (
                <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <Hash className="w-3 h-3" /> {wordCount} words
                  </span>
                  <span className="flex items-center gap-1">
                    <Type className="w-3 h-3" /> {charCount} characters
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3">
            {!extractedText && !isProcessing && (
              <button
                onClick={processOcr}
                disabled={isProcessing}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ScanText className="w-5 h-5" />
                Extract Text
              </button>
            )}

            {extractedText && (
              <>
                <button
                  onClick={copyToClipboard}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 transition-colors"
                >
                  {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                  {copied ? 'Copied!' : 'Copy All'}
                </button>
                <button
                  onClick={downloadAsTxt}
                  className="flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white font-medium rounded-xl hover:bg-green-700 transition-colors"
                >
                  <Download className="w-5 h-5" />
                  Save .txt
                </button>
                <button
                  onClick={() => { setExtractedText(''); setConfidence(null); }}
                  className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 text-gray-600 font-medium rounded-xl hover:bg-gray-200 transition-colors"
                  title="Clear text"
                >
                  <ScanText className="w-5 h-5" />
                  Re-scan
                </button>
              </>
            )}

            <button
              onClick={reset}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-200 text-gray-700 font-medium rounded-xl hover:bg-gray-300 transition-colors"
            >
              <RotateCcw className="w-5 h-5" />
            </button>
          </div>

          {/* Tips */}
          <div>
            <button
              onClick={() => setShowTips(!showTips)}
              className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              <Lightbulb className="w-4 h-4" />
              {showTips ? 'Hide tips' : 'Tips for best results'}
            </button>
            {showTips && (
              <div className="mt-2 bg-amber-50 border border-amber-100 rounded-xl p-4 space-y-2">
                <ul className="text-sm text-amber-800 space-y-1.5">
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-1.5 flex-shrink-0" />
                    Use clear, high-resolution images for best accuracy
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-1.5 flex-shrink-0" />
                    Printed text works much better than handwriting
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-1.5 flex-shrink-0" />
                    Ensure good lighting and minimal shadows
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-1.5 flex-shrink-0" />
                    Crop to the text area to improve accuracy
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-1.5 flex-shrink-0" />
                    Select the correct language for non-English text
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-1.5 flex-shrink-0" />
                    You can edit the extracted text to fix any OCR errors
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageOcr;
